import { useEffect, useMemo, useState } from 'react'
import { BookMarked, Building2, LayoutTemplate, Settings, User } from 'lucide-react'
import { cn } from '../layout.component'
import {
  CameraIcon,
  Expand,
  FavoritesIcon,
  FilesIcon,
  OverviewIcon,
  Plus,
  SpeakersIcon,
  TasksIcon,
  TrashIcon,
} from '../../../assets'
import { useTranslation } from 'react-i18next'
import { NavLink, useNavigate } from 'react-router-dom'
import MeetingModal from './meeting-modal.component'
import KtzhBrand from './ktzh-brand.component'
import Modals from '../../../pages/start-recording/modals/modals.component'
import { Options } from '../../../pages/start-recording/start-recording.page'
import { useMeet } from '../../../contexts/meeting.context'
import { useAuth } from '../../../contexts/auth.context'
import { File, Meet, Teams, Zoom } from '../../../assets/pages'

type NavItem = {
  icon: React.ReactNode;
  label: string;
  to: string;
  step?: number;
  end?: boolean;
};

type NavGroup = {
  titleKey: string;
  items: NavItem[];
};

const navGroups: NavGroup[] = [
  {
    titleKey: 'navigation.group-main',
    items: [
      { icon: <OverviewIcon />, label: 'navigation.overview', to: '/overview', end: true },
      { icon: <FilesIcon />, label: 'navigation.files', to: '/files' },
      { icon: <TasksIcon />, label: 'navigation.tasks', to: '/tasks' },
    ],
  },
  {
    titleKey: 'navigation.group-data',
    items: [
      { icon: <SpeakersIcon />, label: 'navigation.speakers', to: '/speakers' },
      { icon: <FavoritesIcon />, label: 'navigation.favorites', to: '/favorites' },
    ],
  },
  {
    titleKey: 'navigation.group-system',
    items: [
      { icon: <User strokeWidth={1.5} />, label: 'navigation.profile', to: '/profile' },
      { icon: <TrashIcon />, label: 'navigation.trash', to: '/trash' },
    ],
  },
];

export type MeetingOption = Options | 'start';

const Sidebar = () => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();
  const { callActive, meJoined } = useMeet();
  const { user } = useAuth();

  const groups: NavGroup[] = useMemo(() => {
    if (user?.role !== 'admin') return navGroups;
    const aiSettingsItem: NavItem = {
      icon: <Settings strokeWidth={1.5} />,
      label: 'navigation.admin-integrations',
      to: '/admin/ai-settings',
    };
    const templatesItem: NavItem = {
      icon: <LayoutTemplate strokeWidth={1.5} />,
      label: 'navigation.templates',
      to: '/admin/templates',
    };
    const departmentsItem: NavItem = {
      icon: <Building2 strokeWidth={1.5} />,
      label: 'navigation.admin-departments',
      to: '/admin/departments',
    };
    const glossaryItem: NavItem = {
      icon: <BookMarked strokeWidth={1.5} />,
      label: 'navigation.admin-glossary',
      to: '/admin/glossary',
    };
    return navGroups.map(group => group.titleKey === 'navigation.group-system'
      ? { ...group, items: [...group.items, aiSettingsItem, templatesItem, departmentsItem, glossaryItem] }
      : group);
  }, [user]);

  const [showModal, setShowModal] = useState(false);
  const [showSecondModal, setShowSecondModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState<MeetingOption | null>(null);
  const [step, setStep] = useState(0);
  const [showIntro, setShowIntro] = useState(false);

  const closeIntro = () => {
    setShowIntro(false);
    setStep(0);
  };

  useEffect(() => {
    const isFirstVisit = localStorage.getItem('IS_FIRST_VISIT');
    if (!isFirstVisit) {
      setShowIntro(true);
      localStorage.setItem('IS_FIRST_VISIT', 'false');
    }
  }, [])

  const handleStartMeeting = (type: MeetingOption) => {
    setSelectedOption(type);
    setShowModal(false);
    if (type === 'start') {
      // Видеозвонок (страница /meeting) временно скрыт из прода — см. Task 9.
      navigate('/files');
      return;
    }
    setShowSecondModal(true);
  }

  const closeSecondModal = () => {
    setSelectedOption(null);
    setShowSecondModal(false);
  }

  const hints: Record<number, React.ReactNode> = {
    0: <>
      <div className={cn('hint-text')}>
        Создавайте онлайн-встречи, загружайте аудио/видео записи  или подключайте бота к сторонним сервисам
      </div>
      <div className={cn('hint-icons')}>
        <CameraIcon />
        <File />
        <Zoom />
        <Meet />
        <Teams />
      </div>
    </>,
  }

  const Hint = ({ step }: { step: number }) => (
    <div className={cn('hint')}>
      <div className={cn('hint-body')}>
        {hints[step]}
      </div>

      <div className={cn('hint-footer')}>
        <div className={cn('hint-actions')} >
          <button onClick={closeIntro}>
            {t('action.done')}
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className={cn('sidebar', { 'sidebar--collapsed': isCollapsed })}>
      <div className={cn('sidebar__header')}>
        <KtzhBrand collapsed={isCollapsed} />
        <Expand onClick={() => setIsCollapsed(!isCollapsed)} style={{ transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
      </div>
      <div className={cn('sidebar__start-meeting', { 'sidebar__start-meeting--highlighted': showIntro && step === 0 })}>
        <button onClick={() => {
          setShowModal(true)
          setShowSecondModal(false)
        }}
          disabled={callActive && meJoined}
        >
          <Plus /> {!isCollapsed && t('action.start-meeting')}
        </button>

        {showIntro && step === 0 && <Hint step={step} />}
      </div>
      <div className={cn('sidebar__content')}>
        {groups.map(group => (
          <div key={group.titleKey} className={cn('menu-group')}>
            {!isCollapsed && (
              <div className={cn('menu-group__title')}>{t(group.titleKey)}</div>
            )}
            <ul className={cn('menu')}>
              {group.items.map(item => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn('menu__item', {
                        'menu__item--active': isActive,
                        'menu__item--highlight': showIntro && step === item.step,
                      })
                    }
                  >
                    <span className={cn('menu__icon')}>{item.icon}</span>
                    {!isCollapsed && <span className={cn('menu__label')}>{t(item.label)}</span>}
                  </NavLink>

                  {showIntro && step === item.step && <Hint step={step} />}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {showIntro && <div className={cn('overlay')} />}

      <MeetingModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        handleStart={handleStartMeeting}
      />

      {selectedOption
        && selectedOption !== 'start'
        && showSecondModal
        && <Modals
          type={selectedOption}
          setSelectedOption={closeSecondModal}
        />}

    </div>
  )
}

export default Sidebar
